import React, { useState, useEffect } from 'react';
import WorkspaceSidebar from '../../components/ui/WorkspaceSidebar';
import UserProfileDropdown from '../../components/ui/UserProfileDropdown';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import EngagementCard from './components/EngagementCard';
import MetricsCard from './components/MetricsCard';
import ActivityFeed from './components/ActivityFeed';
import QuickActions from './components/QuickActions';
import FilterBar from './components/FilterBar';
import Icon from '../../components/AppIcon';

const AppDashboard = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [filteredEngagements, setFilteredEngagements] = useState([]);
  const [filters, setFilters] = useState({});

  // Mock data for engagements
  const engagements = [
    {
      id: 1,
      name: "S/4HANA Finance Transformation",
      organization: "Enterprise Solutions Ltd",
      lob: "RTR",
      status: "Active",
      progress: 75,
      questionsAnswered: 45,
      fitGapItems: 12,
      documents: 8,
      teamMembers: 6,
      lastActivity: "2h ago"
    },
    {
      id: 2,
      name: "Procurement Optimization",
      organization: "Global Manufacturing Corp",
      lob: "P2P",
      status: "Planning",
      progress: 30,
      questionsAnswered: 18,
      fitGapItems: 5,
      documents: 2,
      teamMembers: 4,
      lastActivity: "1d ago"
    },
    {
      id: 3,
      name: "Order Management Redesign",
      organization: "Tech Innovations Inc",
      lob: "OTC",
      status: "Review",
      progress: 90,
      questionsAnswered: 62,
      fitGapItems: 20,
      documents: 15,
      teamMembers: 8,
      lastActivity: "3h ago"
    },
    {
      id: 4,
      name: "Warehouse Automation",
      organization: "Retail Dynamics Group",
      lob: "EWM",
      status: "Active",
      progress: 55,
      questionsAnswered: 33,
      fitGapItems: 8,
      documents: 5,
      teamMembers: 5,
      lastActivity: "5h ago"
    },
    {
      id: 5,
      name: "Production Planning Enhancement",
      organization: "Global Manufacturing Corp",
      lob: "Plan",
      status: "Completed",
      progress: 100,
      questionsAnswered: 78,
      fitGapItems: 25,
      documents: 22,
      teamMembers: 7,
      lastActivity: "2d ago"
    }
  ];

  // Mock data for metrics
  const metrics = [
    {
      type: "engagements",
      icon: "Building2",
      label: "Active Engagements",
      value: "12",
      change: "+2",
      trend: "up",
      subtitle: "3 new this month"
    },
    {
      type: "assessments",
      icon: "CheckCircle2",
      label: "Completed Assessments",
      value: "48",
      change: "+8",
      trend: "up",
      subtitle: "15% increase from last month"
    },
    {
      type: "approvals",
      icon: "Clock",
      label: "Pending Approvals",
      value: "7",
      change: "-3",
      trend: "down",
      subtitle: "Average 2.3 days to approve"
    },
    {
      type: "documents",
      icon: "FileText",
      label: "Generated Documents",
      value: "156",
      change: "+24",
      trend: "up",
      subtitle: "BPD² and FSD² combined"
    }
  ];

  // Mock data for recent activities
  const activities = [
    {
      id: 1,
      type: "questionnaire",
      user: "Sarah Chen",
      action: "completed questionnaire for",
      target: "Finance Module Assessment",
      engagement: "S/4HANA Finance Transformation",
      timestamp: new Date(Date.now() - 1800000), // 30 minutes ago
      description: "Answered 15 questions about GL configuration requirements"
    },
    {
      id: 2,
      type: "fitgap",
      user: "Michael Rodriguez",
      action: "updated fit/gap analysis for",
      target: "Procurement Workflows",
      engagement: "Procurement Optimization",
      timestamp: new Date(Date.now() - 3600000), // 1 hour ago
      description: "Moved 3 items from Analyzing to Fit status"
    },
    {
      id: 3,
      type: "document",
      user: "Jennifer Park",
      action: "generated BPD² document for",
      target: "Order Processing",
      engagement: "Order Management Redesign",
      timestamp: new Date(Date.now() - 7200000), // 2 hours ago
      description: "Version 2.1 with updated process flows"
    },
    {
      id: 4,
      type: "approval",
      user: "David Kim",
      action: "approved document",
      target: "Warehouse Layout Design",
      engagement: "Warehouse Automation",
      timestamp: new Date(Date.now() - 10800000), // 3 hours ago
      description: "Digital signature applied with compliance notes"
    },
    {
      id: 5,
      type: "engagement",
      user: "Lisa Thompson",
      action: "created new engagement",
      target: "Supply Chain Analytics",
      engagement: "Enterprise Solutions Ltd",
      timestamp: new Date(Date.now() - 14400000), // 4 hours ago
      description: "Initial scope definition for analytics implementation"
    },
    {
      id: 6,
      type: "questionnaire",
      user: "Robert Wilson",
      action: "started questionnaire for",
      target: "Inventory Management",
      engagement: "Warehouse Automation",
      timestamp: new Date(Date.now() - 18000000), // 5 hours ago
      description: "Beginning assessment of current inventory processes"
    }
  ];

  // Filter engagements based on applied filters
  useEffect(() => {
    let filtered = engagements;

    if (filters?.search) {
      filtered = filtered?.filter(engagement =>
        engagement?.name?.toLowerCase()?.includes(filters?.search?.toLowerCase()) ||
        engagement?.organization?.toLowerCase()?.includes(filters?.search?.toLowerCase())
      );
    }

    if (filters?.organization) {
      filtered = filtered?.filter(engagement =>
        engagement?.organization?.toLowerCase()?.includes(filters?.organization?.toLowerCase())
      );
    }

    if (filters?.lob) {
      filtered = filtered?.filter(engagement => engagement?.lob === filters?.lob);
    }

    if (filters?.status) {
      filtered = filtered?.filter(engagement => engagement?.status === filters?.status);
    }

    setFilteredEngagements(filtered);
  }, [filters]);

  // Initialize with all engagements
  useEffect(() => {
    setFilteredEngagements(engagements);
  }, []);

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Sidebar */}
      <WorkspaceSidebar 
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
      />
      {/* Main Content */}
      <div className={`nav-transition ${isSidebarCollapsed ? 'ml-16' : 'ml-64'} md:ml-0 md:pl-${isSidebarCollapsed ? '16' : '64'}`}>
        {/* Top Header */}
        <header className="bg-background border-b border-border px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <h1 className="text-2xl font-bold text-foreground font-inter">
                Dashboard
              </h1>
              <div className="hidden md:flex items-center space-x-2 text-sm text-muted-foreground">
                <Icon name="Calendar" size={16} />
                <span>October 10, 2025</span>
              </div>
            </div>
            <UserProfileDropdown />
          </div>
        </header>

        {/* Page Content */}
        <main className="p-6 pb-20 md:pb-6">
          <BreadcrumbNavigation />

          {/* Welcome Section */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-foreground font-inter mb-2">
              Welcome back, SAP Architect
            </h2>
            <p className="text-muted-foreground">
              Manage your SAP implementation engagements, track progress, and collaborate with your team.
            </p>
          </div>

          {/* Quick Actions */}
          <div className="mb-8">
            <QuickActions />
          </div>

          {/* Metrics Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {metrics?.map((metric, index) => (
              <MetricsCard key={index} metric={metric} />
            ))}
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Engagements Section */}
            <div className="lg:col-span-2">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-foreground font-inter">
                  Active Engagements
                </h3>
                <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                  <Icon name="Filter" size={16} />
                  <span>{filteredEngagements?.length} of {engagements?.length}</span>
                </div>
              </div>

              {/* Filter Bar */}
              <FilterBar onFiltersChange={handleFiltersChange} />

              {/* Engagements Grid */}
              {filteredEngagements?.length > 0 ? (
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                  {filteredEngagements?.map((engagement) => (
                    <EngagementCard key={engagement?.id} engagement={engagement} />
                  ))}
                </div>
              ) : (
                <div className="bg-card border border-border rounded-lg p-12 text-center">
                  <Icon name="Search" size={48} className="text-muted-foreground mx-auto mb-4" />
                  <h4 className="text-lg font-medium text-foreground mb-2">
                    No engagements found
                  </h4>
                  <p className="text-muted-foreground mb-4">
                    Try adjusting your filters or create a new engagement.
                  </p>
                </div>
              )}
            </div>

            {/* Activity Feed */}
            <div className="lg:col-span-1">
              <ActivityFeed activities={activities} />
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default AppDashboard;